/**
 * MONEDA-CONVERSOR.JS — Funciones puras de conversión entre monedas
 *
 * Las tasas se INYECTAN como parámetro — este módulo no hace fetch ni tiene estado.
 * Las tasas son relativas a USD: { PEN: 3.72, BRL: 5.15, EUR: 0.92, ... }
 * (1 USD = 3.72 PEN, 1 USD = 5.15 BRL, etc.)
 *
 * Compatible: ES Modules (fronts) + CommonJS (backend)
 */

import { obtenerPresetPorCodigo, unidadesMenoresPorUnidad } from './moneda-presets.js';
import { formatearCentavosConSimbolo } from './moneda-formateador.js';

// ============================================================
// CONVERSIÓN ENTRE MONEDAS
// ============================================================

/**
 * Calcula la tasa cruzada entre dos monedas
 * @param {string} codigoOrigen - Código ISO origen (ej: 'BRL')
 * @param {string} codigoDestino - Código ISO destino (ej: 'USD')
 * @param {object} tasasBaseUsd - Mapa { PEN: 3.72, BRL: 5.15, USD: 1, ... }
 * @returns {number} Tasa directa (ej: 0.194 para BRL→USD)
 */
export function calcularTasaCruzada(codigoOrigen, codigoDestino, tasasBaseUsd) {
    const tasa = calcularTasaCruzadaONull(codigoOrigen, codigoDestino, tasasBaseUsd);
    // Compat: los callers históricos esperan un número siempre. OJO: el 1 de
    // fallback significa "no sé la tasa", no "valen lo mismo" — mostrar un monto
    // convertido con ese 1 miente (BRL→PYG real es ~1417). Todo caller que
    // MUESTRE dinero debe usar calcularTasaCruzadaONull y avisar ante null.
    return tasa === null ? 1 : tasa;
}

/**
 * Igual que calcularTasaCruzada pero devuelve null cuando la tasa NO se conoce
 * (sin mapa, o falta la de origen/destino, o no es un número positivo).
 * Es la versión honesta: obliga al caller a decidir qué mostrar en vez de
 * inventar una equivalencia 1:1 entre monedas distintas.
 * @returns {number|null}
 */
export function calcularTasaCruzadaONull(codigoOrigen, codigoDestino, tasasBaseUsd) {
    if (codigoOrigen === codigoDestino) return 1;
    if (!tasasBaseUsd) return null;

    const tasaOrigen = tasasBaseUsd[codigoOrigen];
    const tasaDestino = tasasBaseUsd[codigoDestino];
    if (!_esTasaUsable(tasaOrigen) || !_esTasaUsable(tasaDestino)) return null;

    // origen→USD→destino: dividir por tasa origen (a USD), multiplicar por tasa destino
    return tasaDestino / tasaOrigen;
}

/** Una tasa solo sirve si es número finito y positivo (la DB no lo garantiza). */
function _esTasaUsable(tasa) {
    return typeof tasa === 'number' && Number.isFinite(tasa) && tasa > 0;
}

/**
 * Convierte centavos de una moneda a otra
 * @param {number} centavos - Monto en centavos de la moneda origen
 * @param {string} codigoOrigen - Código ISO origen
 * @param {string} codigoDestino - Código ISO destino
 * @param {object} tasasBaseUsd - Mapa de tasas relativas a USD
 * @returns {number} Centavos en la moneda destino (redondeado)
 */
export function convertirCentavosEntreMonedas(centavos, codigoOrigen, codigoDestino, tasasBaseUsd) {
    if (codigoOrigen === codigoDestino) return centavos;

    // La tasa cruzada es por UNIDAD de moneda; convertir vía unidades display y
    // reescalar por el factor de cada moneda (100 BRL/PEN, 1 PYG/COP). Multiplicar
    // unidades menores directo solo era válido entre monedas del mismo decimalaje
    // (₲100.000 → habría dado R$0,68 en vez de R$686).
    const tasa = calcularTasaCruzada(codigoOrigen, codigoDestino, tasasBaseUsd);
    const unidadesOrigen = centavos / unidadesMenoresPorUnidad(codigoOrigen);
    return Math.round(unidadesOrigen * tasa * unidadesMenoresPorUnidad(codigoDestino));
}

/**
 * Formatea una conversión aproximada con símbolo ≈
 * @param {number} centavos - Monto en centavos de la moneda origen
 * @param {string} codigoOrigen - Código ISO origen
 * @param {string} codigoDestino - Código ISO destino
 * @param {object} tasasBaseUsd - Mapa de tasas relativas a USD
 * @returns {string} Ej: "≈ $ 9.20"
 */
export function formatearConversionAproximada(centavos, codigoOrigen, codigoDestino, tasasBaseUsd) {
    const centavosConvertidos = convertirCentavosEntreMonedas(centavos, codigoOrigen, codigoDestino, tasasBaseUsd);
    return '≈ ' + formatearCentavosConSimbolo(centavosConvertidos, codigoDestino);
}

// ============================================================
// COMMONJS COMPATIBILITY
// ============================================================

if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        calcularTasaCruzada,
        calcularTasaCruzadaONull,
        convertirCentavosEntreMonedas,
        formatearConversionAproximada,
    };
}
